/**
 * HelpTooltip
 * A question-mark icon that shows contextual help text.
 * Uses a click-toggle popover so it works on both desktop and mobile.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { cn } from '@/lib/utils';

interface HelpTooltipProps {
  /** The help text to display. Supports line breaks via '\n'. */
  text: string;
  /** Optional className for the trigger button */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Max width of the popover in pixels */
  maxWidth?: number;
}

export function HelpTooltip({
  text,
  className,
  size = 'sm',
  maxWidth = 320,
}: HelpTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number; arrowLeft: number } | null>(null);

  // Recompute on open, before paint (useLayoutEffect avoids a flash at the wrong position):
  // a centered popover would otherwise run off-screen whenever the trigger sits near a
  // viewport edge (e.g. the right-hand column of a 2-column grid on a narrow phone), since
  // the default `left-1/2 -translate-x-1/2` centering knows nothing about the viewport.
  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setPos(null);
      return;
    }
    const margin = 16;
    const width = Math.min(maxWidth, window.innerWidth - margin * 2);
    const wrapperRect = ref.current.getBoundingClientRect();
    const triggerCenter = wrapperRect.left + wrapperRect.width / 2;
    const desiredLeft = triggerCenter - width / 2;
    const clampedViewportLeft = Math.max(margin, Math.min(desiredLeft, window.innerWidth - width - margin));
    setPos({
      left: clampedViewportLeft - wrapperRect.left,
      width,
      // Keep the arrow pointing at the trigger even when the box itself had to shift.
      arrowLeft: triggerCenter - clampedViewportLeft,
    });
  }, [open, maxWidth]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const iconSize = size === 'sm' ? 'h-4 w-4 text-[10px]' : 'h-5 w-5 text-xs';

  return (
    <div ref={ref} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'inline-flex items-center justify-center rounded-full border border-muted-foreground/30 text-muted-foreground hover:text-foreground hover:border-foreground/50 transition-colors bg-transparent cursor-pointer',
          iconSize
        )}
        aria-label="Help"
        aria-expanded={open}
      >
        ?
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute z-50 top-full mt-2 rounded-md border border-border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
          // `width` (not just `maxWidth`) is required here: this popover's containing block is
          // its own trigger button (a ~16px circle), so an absolutely positioned element with
          // `width: auto` shrink-to-fits against THAT tiny box, not the viewport — collapsing to
          // a near-unreadable single-word-per-line column no matter how large `maxWidth` is.
          // `pos` (computed in the layout effect above) supplies both the clamped width and a
          // viewport-safe `left`; until it's measured, fall back to a centered position at the
          // requested width so nothing ever renders using the broken shrink-to-fit behavior.
          style={
            pos
              ? { width: pos.width, left: pos.left }
              : { width: maxWidth, left: '50%', transform: 'translateX(-50%)' }
          }
        >
          {/* Arrow — offset to keep pointing at the trigger even when the box shifted to stay on-screen */}
          <div
            className="absolute -top-1 h-2 w-2 rotate-45 border-l border-t border-border bg-popover"
            style={pos ? { left: pos.arrowLeft - 4 } : { left: '50%', transform: 'translateX(-50%)' }}
          />
          <div className="relative whitespace-pre-line leading-relaxed">
            {text}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * HelpBlock
 * An inline help panel for longer explanations that should be visible
 * without clicking. Collapsible via a "Learn more" toggle.
 */

interface HelpBlockProps {
  /** Short summary always visible */
  summary: string;
  /** Longer details shown when expanded */
  details?: string;
  /** Optional className */
  className?: string;
}

export function HelpBlock({ summary, details, className }: HelpBlockProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={cn(
        'rounded-md border border-border/60 bg-accent/30 px-3 py-2.5 text-xs text-muted-foreground leading-relaxed',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 inline-flex items-center justify-center rounded-full border border-muted-foreground/30 h-4 w-4 text-[10px] text-muted-foreground">
          ?
        </span>
        <div className="flex-1">
          <span>{summary}</span>
          {details && (
            <>
              {expanded && (
                <p className="mt-2 m-0 whitespace-pre-line">{details}</p>
              )}
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="ml-1 text-primary hover:underline bg-transparent border-0 cursor-pointer p-0 text-xs"
              >
                {expanded ? 'Show less' : 'Learn more'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
